import React, { useState } from 'react';
import { BarChart2, Droplet, Plus, ArrowUp, ArrowDown, Check } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент трекера потребления воды для отображения в боковой панели
 */
const WaterTrackerSidebar = ({ 
  waterConsumptionData, 
  updateWaterConsumptionData, 
  recommendedAmount,
  formData 
}) => {
  const [quickAddAmount, setQuickAddAmount] = useState(250); // объем для быстрого добавления
  const [isQuickAddExpanded, setIsQuickAddExpanded] = useState(false);
  
  // Определяем текущий день
  const today = new Date().toISOString().split('T')[0];
  
  // Получаем данные по текущему дню
  const todayData = waterConsumptionData.find(item => item.date === today) || { date: today, amount: 0 };
  
  // Сортируем данные по дате (от новых к старым) и берем последние 7 дней
  const recentData = [...waterConsumptionData]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 7);
  
  // Рассчитываем процент выполнения нормы на сегодня
  const progressPercent = Math.min(100, Math.round((todayData.amount / recommendedAmount) * 100));
  
  // Быстрое добавление воды
  const handleQuickAdd = (amount) => {
    updateWaterConsumptionData(today, amount);
  };
  
  // Сброс данных за сегодня
  const handleResetToday = () => {
    // Фильтруем данные, исключая сегодняшний день
    updateWaterConsumptionData(today, -todayData.amount);
  };
  
  // Получаем класс для цвета заполнения (зависит от прогресса)
  const getProgressColorClass = (percent) => {
    if (percent < 25) return 'bg-red-500';
    if (percent < 50) return 'bg-orange-500';
    if (percent < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  // Предопределенные объемы для быстрого добавления
  const commonSizes = [
    { label: 'Стакан', value: 250 },
    { label: 'Кружка', value: 350 },
    { label: 'Бутылка', value: 500 }
  ];

  // Предпочитаемый размер из настроек
  const preferredSize = parseInt(formData.waterIntakePreferences.preferredContainerSize) || 250;
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <BarChart2 className="w-5 h-5 mr-2 text-blue-500" />
        Трекер потребления воды
      </h3>
      
      {/* Сегодняшний прогресс */}
      <div className="mb-5">
        <div className="flex justify-between items-center mb-1">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Прогресс сегодня
          </div>
          <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
            {formatNumber(todayData.amount, 0)} / {formatNumber(recommendedAmount, 0)} мл
          </div>
        </div>
        
        {/* Прогресс-бар */}
        <div className="relative w-full h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className={`h-4 rounded-full transition-all duration-500 ${getProgressColorClass(progressPercent)}`} 
            style={{ width: `${progressPercent}%` }}
          ></div>
          
          {/* Отметки на шкале */}
          <div className="absolute top-0 left-0 w-full h-full flex justify-between px-1 pointer-events-none">
            {[25, 50, 75].map(mark => (
              <div key={mark} className="h-full w-px bg-gray-300 dark:bg-gray-600 opacity-50"></div>
            ))}
          </div>
        </div>
        
        {/* Текстовый прогресс и мотивационное сообщение */}
        <div className="mt-1 flex justify-between text-xs">
          <div className="text-gray-500 dark:text-gray-400">
            {progressPercent}% от дневной нормы
          </div>
          <div className={`font-medium ${getTextColorClass(progressPercent)}`}>
            {getProgressMessage(progressPercent)}
          </div>
        </div>
      </div>
      
      {/* Быстрое добавление */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1">
            <Droplet className="w-4 h-4 text-blue-500" />
            <span>Быстрое добавление</span>
          </div>
          <button 
            onClick={() => setIsQuickAddExpanded(!isQuickAddExpanded)}
            className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-0.5"
          >
            {isQuickAddExpanded ? (
              <>
                <ArrowUp className="w-3 h-3" /> Скрыть
              </>
            ) : (
              <>
                <ArrowDown className="w-3 h-3" /> Показать больше
              </>
            )}
          </button>
        </div>
        
        {/* Основные размеры */}
        <div className="grid grid-cols-3 gap-2 mb-2">
          {commonSizes.map(size => (
            <button
              key={size.value}
              onClick={() => handleQuickAdd(size.value)}
              className={`p-2 text-xs rounded-lg border transition-colors flex flex-col items-center
                ${size.value === preferredSize 
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <Droplet className="w-4 h-4 mb-1" />
              <span className="font-medium">{size.label}</span>
              <span>{size.value} мл</span>
            </button>
          ))}
        </div>
        
        {/* Дополнительные объемы (расширенный режим) */}
        {isQuickAddExpanded && (
          <div className="space-y-2 mb-2">
            {/* Пользовательский ввод */}
            <div className="flex gap-2">
              <input
                type="number"
                value={quickAddAmount}
                onChange={e => setQuickAddAmount(parseInt(e.target.value) || 0)}
                className="form-input flex-grow py-1 text-sm"
                placeholder="Введите объем в мл"
                min="10"
                max="2000"
                step="10"
              />
              <Button
                size="sm"
                onClick={() => handleQuickAdd(quickAddAmount)}
                disabled={quickAddAmount <= 0}
                icon={<Plus className="w-4 h-4" />}
              >
                Добавить
              </Button>
            </div>
            
            {/* Дополнительные предустановленные объемы */}
            <div className="grid grid-cols-4 gap-1">
              {[100, 150, 200, 1000].map(amount => (
                <button
                  key={amount}
                  onClick={() => handleQuickAdd(amount)}
                  className="px-1 py-1 text-xs rounded border border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                >
                  {amount} мл
                </button>
              ))}
            </div>
            
            {/* Сбросить сегодняшние данные */}
            {todayData.amount > 0 && (
              <button 
                onClick={handleResetToday}
                className="w-full text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors mt-1 text-center"
              >
                Сбросить данные за сегодня
              </button>
            )}
          </div>
        )}
      </div>
      
      {/* История потребления */}
      <div>
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          История потребления
        </div>
        
        {recentData.length === 0 ? (
          <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
            История потребления воды отсутствует.<br />
            Начните отслеживание сегодня!
          </div>
        ) : (
          <div className="space-y-2">
            {recentData.map(day => {
              const date = new Date(day.date);
              const isToday = day.date === today;
              const percent = Math.min(100, Math.round((day.amount / recommendedAmount) * 100));
              
              return (
                <div 
                  key={day.date} 
                  className={`p-2 rounded-lg border ${isToday ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div className="text-xs font-medium">
                      {isToday ? 'Сегодня' : new Date(day.date).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'numeric' })}
                    </div>
                    <div className="text-xs">
                      {formatNumber(day.amount, 0)} мл <span className="text-gray-500 dark:text-gray-400">({percent}%)</span>
                    </div>
                  </div>
                  
                  {/* Прогресс-бар для дня */}
                  <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-2 rounded-full ${getProgressColorClass(percent)}`} 
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                  
                  {/* Отметка о достижении цели */}
                  {percent >= 100 && (
                    <div className="mt-1 text-xs text-green-600 dark:text-green-400 flex items-center justify-end gap-1">
                      <Check className="w-3 h-3" /> Норма выполнена
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Вспомогательная функция для определения цвета текста в зависимости от прогресса
const getTextColorClass = (percent) => {
  if (percent < 25) return 'text-red-600 dark:text-red-400';
  if (percent < 50) return 'text-orange-600 dark:text-orange-400';
  if (percent < 75) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
};

// Вспомогательная функция для получения мотивационного сообщения
const getProgressMessage = (percent) => {
  if (percent === 0) return 'Начните пить воду!';
  if (percent < 25) return 'Нужно больше воды!';
  if (percent < 50) return 'Хорошее начало!';
  if (percent < 75) return 'Уже больше половины!';
  if (percent < 100) return 'Почти выполнено!';
  return 'Отличная работа!';
};

export default WaterTrackerSidebar;